import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config/config';

const AuthContext = createContext(null);

const TOKEN_KEY = 'depguard_token';
const USER_KEY = 'depguard_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  // Validate the stored token on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${config.BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(u => { setUser(u); localStorage.setItem(USER_KEY, JSON.stringify(u)); })
      .catch(() => { logout(); })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySession = useCallback((data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${config.BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Login failed');
    }
    const data = await res.json();
    return applySession(data);
  }, [applySession]);

  const loginWithToken = useCallback(async (incomingToken) => {
    localStorage.setItem(TOKEN_KEY, incomingToken);
    setToken(incomingToken);
    const res = await fetch(`${config.BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${incomingToken}` },
    });
    if (!res.ok) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      throw new Error('Enterprise sign-in failed');
    }
    const userData = await res.json();
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const register = useCallback(async (email, password, role) => {
    const res = await fetch(`${config.BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ email, password, role }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Registration failed');
    }
    const data = await res.json();
    // If no user yet (first registration), log in automatically
    if (!user) {
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
    }
    return data.user;
  }, [token, user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithToken, logout, register, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Attach token to all fetch calls automatically
export function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
